import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { ActionService } from "../../../../services/action.service";
import { EvidenceLayerService } from "../../../../services/evidence-layer.service";
import { FileUploadService } from "../../../../services/file-upload.service";
import { DataDetailDialogComponent } from "../../shared/data-detail-dialog/data-detail-dialog.component";
import type { ContentBlockData } from "../../../../model/block.types";

/**
 * Inline hint chip for the plan-level workarea geometry block (workarea-ui-spec.md §3; ODD
 * plan-ui-doc31 W4b user ruling: HINT chip like the geofencing/geo hint chip, NOT a full grid
 * card). Minimal informational surface — label + map icon with three actions:
 * - Zoom to: reads `metadata.bbox` directly (no fetch) via the same ActionService path the
 *   geo hint chip uses (`zoom_to` with bounds).
 * - View data: fetches `ref.url` via FileUploadService and opens the shared
 *   DataDetailDialogComponent (same mechanics as OutputRefChipComponent.view).
 * - Add to map (evidence-layer-ui US5): MANUAL add through `EvidenceLayerService.addToMapFromBlock`
 *   with the whole block — style by `layer.type`, layer name `layer.label ?? data.label` and the
 *   plan layer group via `layer.plan_ref` are the service's concern. There is NO auto-draw
 *   anywhere (user ruling: the workarea must be added by hand, never automatically).
 * No download/copy actions — the workarea is informativa. Errors are logged (console.error),
 * the dialog simply stays closed (degradation rule: never fatal).
 */
@Component({
  selector: "app-workarea-hint-chip",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatMenu, MatMenuItem, MatMenuTrigger],
  templateUrl: "./workarea-hint-chip.component.html",
  styleUrl: "./workarea-hint-chip.component.scss",
})
export class WorkareaHintChipComponent {
  readonly data = input.required<ContentBlockData>();

  readonly #actions = inject(ActionService);

  readonly #evidenceLayer = inject(EvidenceLayerService);

  readonly #fileUpload = inject(FileUploadService);

  readonly #dialog = inject(MatDialog);

  protected readonly viewing = signal(false);

  protected readonly adding = signal(false);

  /** Fixed English copy — the chip always represents the plan-level workarea. */
  protected readonly label = "Plan workarea";

  protected readonly bbox = computed(
    () => (this.data().metadata ?? {})["bbox"] as [number, number, number, number] | undefined,
  );

  protected zoomTo(): void {
    const bbox = this.bbox();
    if (!bbox) return;
    this.#actions.execute("zoom_to", { bounds: bbox });
  }

  /** Manual add (evidence-layer-ui US5): one in-flight add at a time, never fatal. */
  protected addToMap(): void {
    if (this.adding()) return;
    this.adding.set(true);
    this.#evidenceLayer
      .addToMapFromBlock(this.data())
      .catch((err) => console.error("WorkareaHintChipComponent: add to map failed", err))
      .finally(() => this.adding.set(false));
  }

  protected view(): void {
    if (this.viewing()) return;
    const { ref } = this.data();
    if (!ref) {
      console.error("WorkareaHintChipComponent: view requested but the block has no file ref");
      return;
    }
    this.viewing.set(true);
    firstValueFrom(
      this.#fileUpload.fetchFileContent(ref.url, ref.mime_type, {
        filename: ref.filename,
        size_bytes: ref.size_bytes,
      }),
    )
      .then((result) => {
        this.#dialog.open(DataDetailDialogComponent, {
          data: { data: result, title: ref.filename },
          width: "90vw",
          maxWidth: "1000px",
          minHeight: "350px",
          maxHeight: "90vh",
        });
      })
      .catch((err) => console.error("WorkareaHintChipComponent: view fetch failed", err))
      .finally(() => this.viewing.set(false));
  }
}
